/**
 * Client-only constants, and the one browser-side formatter.
 *
 * Everything the web and the server must agree on lives in the generated
 * `./const.js`, which this module is re-exported from so `import * as K` stays one
 * namespace. What is here has no server counterpart: the chunk cadence, the chirp
 * taper, the capture-gap rule and the retention formatter. Those are properties of
 * *this device's* behaviour, so there is nothing to mirror -- only measurements and
 * reasons worth keeping beside the numbers.
 */

// The scoring grid's frame length, in seconds. It is the contract's
// `MERGE_FRAME_SEC` under the name the browser's scoring path has always used.
export const FRAME_SEC = 1.0;

// How often claim generation runs during recording. §4.2: it runs concurrently
// with capture so the claim is ready the moment the session ends.
export const CLAIM_CHUNK_SEC = 20.0;

// [M] Window hop for the *final* chunk only, scored while somebody watches the button.
//
// The tail is bounded by CLAIM_CHUNK_SEC, so up to 20 s of audio is scored after the stop
// press: measured at 3 s on one phone and 17 s on another. A coarser hop halves the
// windows and so halves that wait.
//
// Shrinking CLAIM_CHUNK_SEC would be the obvious fix and it is the wrong one: the 2 s
// inter-chunk overlap is fixed, so 5 s chunks turn 10% redundant scoring into 40%, and
// the slower phone -- already at ~88% duty just keeping up -- would fall behind
// permanently. This moves nothing into the session; it just does less work at the end.
//
// What it costs: turn boundaries in the last seconds are placed to 2 s rather than 1 s.
// That is the cheapest precision in the system to give away.
export const FINAL_CHUNK_HOP_SEC = 2.0;

// [A] Gap between repeats when the chirp is left broadcasting. One burst is ~1 s,
// so this is a ~17% duty cycle rather than a continuous tone.
//
// It is deliberately not continuous. The receiver only needs to catch one frame,
// and everything that makes a permanent 18 kHz emission a bad idea near a
// microphone -- automatic gain control reacting to energy it cannot hear, an audio
// session flipping into echo-cancelling mode, audibility to the under-25s and the
// dog -- scales with how much of the time the tone is actually present.
export const CHIRP_REPEAT_SEC = 6.0;

// [A] The join window, and the gap used inside it. Broadcasting starts dense and
// backs off, because the two costs of a chirp live on different axes:
//
//   - Acquisition time depends on the gap *right now*. [D] A listener arrives at a
//     uniform point in the cycle, so mean wait is cycle/2 + 0.84 s: 4.33 s at a 6 s
//     gap, 2.33 s at 2 s.
//   - AGC contamination depends on the *integral* over the session -- how much of
//     the recording has an 18 kHz tone present near a live microphone.
//
// [D] So 2 s for 45 s and 6 s thereafter costs 15.5% duty over an 11-minute session
// against 14.2% for a flat 6 s -- essentially unchanged -- while nearly halving the
// join time during the window when people are actually joining. A flat 2.5 s gap
// buys a similar join time for 28.4% duty, paying for the join window across the
// whole meeting when 95% of that emission is after everyone is in.
//
// The cadence backs off rather than stopping, because people do arrive late.
//
// [T] The risk this trade carries, and the flat reduction does not: it concentrates
// ultrasonic energy into the first 45 s, which is the window §5's alignment
// cross-correlates over, on the reference device. The tone cannot reach the
// embedding (`resample` low-passes before decimation), but hardware AGC acts before
// anything this app controls. Unmeasured in either direction -- `tools/README.md`
// sketches the gain-riding probe that would settle it. If it comes back badly, set
// CHIRP_DENSE_SEC to 0 and the taper is gone.
export const CHIRP_DENSE_SEC = 45.0;
export const CHIRP_DENSE_REPEAT_SEC = 2.0;

// [A] Below this, a shortfall between the wall clock and the frames the microphone
// actually delivered is ordinary jitter -- startup latency, buffering, audio-clock
// drift. Above it, capture was suspended, and the recording has a hole in it.
//
// The margin is wide: a sound card off by 100 ppm accumulates 66 ms over an
// 11-minute session, so 0.5 s is roughly 7x the worst honest drift, while still
// catching the shortest suspension worth knowing about.
export const CAPTURE_GAP_MIN_SEC = 0.5;

/**
 * How much of an interval must be missing before capture counts as stopped.
 *
 * [M] The 100 ppm budget above is out by a factor of 390, and this is what replaces the
 * reasoning rather than the number. Measured drift between `ctx.currentTime` and
 * `performance.now()` on real handsets is **~3.9%** -- 39,000 ppm -- so the shortfall
 * crosses `CAPTURE_GAP_MIN_SEC` every ~13 s of recording, forever, on a microphone that
 * never missed a sample. Every "gap" the field has ever recorded says so: 326 of them
 * across five handsets, and on one phone 186 of 189 within 3 ms of each other, all sitting
 * in the 57 ms band immediately above the threshold. A locked screen does not do that. A
 * counter tripping does.
 *
 * The lesson is that **size cannot tell drift from a suspension, and rate can.** Drift is a
 * small fraction of any interval no matter how long you wait; a stopped microphone is
 * nearly all of it. So the question moved from "are we half a second short?" to "did the
 * microphone deliver anything over the last interval?"
 *
 * 0.5 sits ~13x above measured drift and 2x below a total stop, and -- because it is a
 * ratio -- it does not care how far apart two checks land. That matters: a throttled
 * background tick can be 30 s apart, and 3.9% of 30 s clears the absolute bar above
 * comfortably while nothing whatsoever has gone wrong.
 */
export const CAPTURE_STOP_FRACTION = 0.5;

/**
 * `audio_retention_hours` as a person reads it, for the line under the wordmark.
 *
 * The one function in a file of constants, and it earns the exception the same way
 * `format_retention` does in `meetfolks/constants.py`: `/v1/config` reports a *number*
 * of hours, so every reader of it formats its own way, and the wire format leaves no
 * way to avoid the duplication. `tests/test_constants.py` holds the two in step.
 *
 * Why format at all: this string is read *before* somebody agrees to be recorded, and
 * "deleted after 720h" makes a 30-day window into arithmetic. Whole days become days;
 * anything else stays in hours rather than rounding into a friendlier number than the
 * truth -- 36 hours is not "1 day" and not "2 days".
 */
export function formatRetention(hours) {
  if (hours % 24 === 0 && hours >= 24) {
    const days = hours / 24;
    return days === 1 ? "1 day" : `${days} days`;
  }
  return `${hours}h`;
}
